import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ApiService } from './services/api';

const EmailVerificationPage = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('');
    const hasAttemptedRef = useRef(false);

    useEffect(() => {
        const verifyEmail = async () => {
            // Prevent duplicate verification attempts
            if (hasAttemptedRef.current) {
                return;
            }

            const token = searchParams.get('token');

            if (!token) {
                setStatus('error');
                setMessage('No verification token provided.');
                hasAttemptedRef.current = true;
                return;
            }

            hasAttemptedRef.current = true;

            try {
                const data = await ApiService.verifyEmail(token);
                setStatus('success');
                setMessage(data.message || 'Email verified successfully!');
                toast.success(data.message || 'Email verified successfully! You can now log in.');
            } catch (error: any) {
                setStatus('error');
                setMessage(error.message || 'An error occurred during verification.');
                toast.error(error.message || 'An error occurred during verification.');
            }
        };

        verifyEmail();
    }, [searchParams]);

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">
            <div className="w-full max-w-md p-8 space-y-6 bg-gray-800 rounded-lg shadow-lg text-center">
                {status === 'loading' && (
                    <>
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
                        <h1 className="text-xl font-semibold">Verifying your email...</h1>
                        <p className="text-gray-400">Please wait while we verify your email address.</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h1 className="text-xl font-semibold text-green-400">Email Verified!</h1>
                        <p className="text-gray-300">{message}</p>
                        <Link
                            to="/login"
                            className="inline-block px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                        >
                            Go to Login
                        </Link>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h1 className="text-xl font-semibold text-red-400">Verification Failed</h1>
                        <p className="text-gray-300">{message}</p>
                        <div className="space-y-2">
                            <Link
                                to="/login"
                                className="inline-block px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                            >
                                Go to Login
                            </Link>
                            <p className="text-sm text-gray-400">
                                Need a new verification email?{' '}
                                <Link to="/login" className="text-purple-400 hover:underline">
                                    Try logging in
                                </Link>
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default EmailVerificationPage; 